import React, { useState } from "react";
import { FileText, Image, File, Download, X, Eye } from "lucide-react";
import FileViewerModal from "./FileViewerModal";
import "./FilePreview.scss";

const FILE_ICONS = {
  "application/pdf": FileText,
  "text/plain": FileText,
  "application/msword": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "image/jpeg": Image,
  "image/png": Image,
  "image/webp": Image,
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const isImage = (type) => type?.startsWith("image/");

/**
 * FilePreview — Display list of uploaded file attachments.
 * Clicking a file opens it in the in-app FileViewerModal.
 *
 * Props:
 *   attachments   — [{ fileName, fileUrl, fileType, fileSize, fileKey }]
 *   onRemove      — (fileKey) => void  (if provided, shows remove button)
 *   compact       — compact layout (default: false)
 *   className     — extra class
 */
const FilePreview = ({ attachments = [], onRemove, compact = false, className = "" }) => {
  const [viewerFile, setViewerFile] = useState(null);

  if (!attachments.length) return null;

  const getIcon = (type) => {
    const Icon = FILE_ICONS[type] || File;
    return <Icon size={compact ? 16 : 18} />;
  };

  const openViewer = (att) => {
    if (att.fileUrl) setViewerFile(att);
  };

  return (
    <>
      <div className={`file-preview ${compact ? "file-preview--compact" : ""} ${className}`}>
        {attachments.map((att, idx) => (
          <div key={att.fileKey || idx} className="file-preview__item">
            {isImage(att.fileType) && att.fileUrl ? (
              <button
                type="button"
                className="file-preview__thumb-link"
                onClick={() => openViewer(att)}
              >
                <img
                  src={att.fileUrl}
                  alt={att.fileName}
                  className="file-preview__thumb"
                  loading="lazy"
                />
              </button>
            ) : (
              <span className="file-preview__icon">{getIcon(att.fileType)}</span>
            )}

            <div className="file-preview__info">
              <button
                type="button"
                className="file-preview__name-btn"
                onClick={() => openViewer(att)}
                title={att.fileName}
              >
                {att.fileName}
              </button>
              {att.fileSize > 0 && (
                <span className="file-preview__size">{formatFileSize(att.fileSize)}</span>
              )}
            </div>

            <div className="file-preview__actions">
              {att.fileUrl && (
                <button
                  type="button"
                  className="file-preview__action-btn"
                  onClick={() => openViewer(att)}
                  title="View"
                >
                  <Eye size={14} />
                </button>
              )}
              {att.fileUrl && (
                <a
                  href={att.fileUrl}
                  download={att.fileName}
                  className="file-preview__action-btn"
                  title="Download"
                >
                  <Download size={14} />
                </a>
              )}
              {onRemove && (
                <button
                  type="button"
                  className="file-preview__action-btn file-preview__action-btn--remove"
                  onClick={() => onRemove(att.fileKey)}
                  title="Remove"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <FileViewerModal
        show={!!viewerFile}
        onHide={() => setViewerFile(null)}
        fileUrl={viewerFile?.fileUrl}
        fileName={viewerFile?.fileName}
        fileType={viewerFile?.fileType}
      />
    </>
  );
};

export default FilePreview;
